/**
 * Marks bookkeeping for a fan's recording: every game event that arrives while the
 * camera rolls is stamped with its offset into the recording. Pure + unit-tested;
 * the recorder hook just feeds it `now()` values.
 */
export type Mark = { seq: number; offsetMs: number };

export type MarkBook = {
  readonly startedAt: number; // performance/Date ms when recording started
  readonly startedAtSeq: number; // game seq applied when recording started (-1 before tip)
  marks: Mark[];
};

export const MAX_RECORDING_MS = 120_000;
export const QUICK_TAKE_MS = 10_000;

export function startMarks(startedAt: number, startedAtSeq: number): MarkBook {
  return { startedAt, startedAtSeq, marks: [] };
}

/**
 * Record that game event `seq` arrived at `now`. Ignores events already applied when recording
 * started, duplicates (re-delivered over a reconnect) and anything past the recording limit.
 * Offsets are integers and never decrease (a burst after a seek shares one offset).
 */
export function addMark(book: MarkBook, seq: number, now: number, maxMs = MAX_RECORDING_MS): MarkBook {
  if (seq <= book.startedAtSeq) return book;
  if (book.marks.some((m) => m.seq === seq)) return book;
  const prev = book.marks[book.marks.length - 1];
  let offsetMs = Math.max(0, Math.round(now - book.startedAt));
  if (offsetMs > maxMs) return book;
  if (prev && offsetMs < prev.offsetMs) offsetMs = prev.offsetMs;
  return { ...book, marks: [...book.marks, { seq, offsetMs }] };
}

/** Final marks for a recording of `durationMs`: sorted by seq, clipped to the recording. */
export function finishMarks(book: MarkBook, durationMs: number): Mark[] {
  return book.marks.filter((m) => m.offsetMs <= durationMs).sort((a, b) => a.seq - b.seq);
}

/** The recording offset (ms) at which `seq` is on screen: its own mark, else the last mark before it. */
export function offsetForSeq(marks: readonly Mark[], seq: number): number | undefined {
  let best: Mark | undefined;
  for (const m of marks) if (m.seq <= seq && (!best || m.seq > best.seq)) best = m;
  return best?.offsetMs;
}

/** Does a recording cover this play? (it arrived while the camera rolled) */
export function covers(marks: readonly Mark[], seq: number): boolean {
  return marks.some((m) => m.seq === seq);
}

/** "0:07" style timer for the REC pill. */
export function recClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
